import React, { useState, useEffect } from 'react';
import { TextField, Paper, Typography, Box, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import { useSnackbar } from '../SnackbarProvider';
import MyButton from '../MyButton';
import { ButtonType } from '../../constants/enums';
import { WorkflowEtape, GeneriqueData } from '../../models/models';
import {
  saveWorkflowEtape,
  deleteWorkflowEtape,
  setSelectedWorkflowEtape,
} from '../../reduxStore/WorkflowEtapeSlice';

interface WorkflowEtapeFormProps {
  workflows: GeneriqueData[];
  onSuccess: () => void;
}

const WorkflowEtapeForm: React.FC<WorkflowEtapeFormProps> = ({ workflows, onSuccess }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { selectedWorkflowEtape } = useSelector((state: any) => state.WorkflowEtapeSlice);
  const { sysTypeData } = useSelector((state: RootState) => state.SysTypeDocumentSlice);

  const getInitialData = (): WorkflowEtape => ({
    workflow_id: workflows[0]?.id ?? 0,
    doc_source_code: '',
    doc_cible_code: '',
    ordre: 0,
  });

  const [formData, setFormData] = useState<WorkflowEtape>(getInitialData);

  useEffect(() => {
    if (selectedWorkflowEtape) {
      setFormData(selectedWorkflowEtape);
    } else {
      setFormData(getInitialData());
    }
  }, [selectedWorkflowEtape, workflows]);

  const reset = () => {
    dispatch(setSelectedWorkflowEtape(null));
    setFormData(getInitialData());
  };

  const handleSubmit = async () => {
    if (!formData.workflow_id || !formData.doc_source_code || !formData.doc_cible_code) {
      showSnackbar('Veuillez remplir tous les champs obligatoires', 'warning');
      return;
    }
    try {
      await dispatch(saveWorkflowEtape(formData)).unwrap();
      showSnackbar('Étape enregistrée', 'success');
      onSuccess();
      reset();
    } catch (error) {
      showSnackbar(error as string || 'Erreur lors de l\'enregistrement', 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedWorkflowEtape?.id) return;
    if (!window.confirm('Confirmer la suppression ?')) return;
    try {
      await dispatch(deleteWorkflowEtape(selectedWorkflowEtape.id)).unwrap();
      showSnackbar('Étape supprimée', 'success');
      onSuccess();
      reset();
    } catch (error) {
      showSnackbar(error as string || 'Échec de la suppression', 'error');
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {selectedWorkflowEtape ? 'Modifier une transition' : 'Ajouter une transition'}
      </Typography>

      <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
        {/* Workflow parent */}
        <FormControl fullWidth size="small" required sx={{ gridColumn: 'span 2' }}>
          <InputLabel>Workflow</InputLabel>
          <Select
            label="Workflow"
            value={formData.workflow_id || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, workflow_id: Number(e.target.value) }))}
          >
            {workflows.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.nom}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Document source */}
        <FormControl fullWidth size="small" required>
          <InputLabel>Document source</InputLabel>
          <Select
            label="Document source"
            value={formData.doc_source_code || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, doc_source_code: e.target.value as string }))}
          >
            {sysTypeData.map(t => (
              <MenuItem key={t.code} value={t.code}>{t.libelle}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Document cible */}
        <FormControl fullWidth size="small" required>
          <InputLabel>Document cible</InputLabel>
          <Select
            label="Document cible"
            value={formData.doc_cible_code || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, doc_cible_code: e.target.value as string }))}
          >
            {sysTypeData
              .filter(t => t.code !== formData.doc_source_code)
              .map(t => (
                <MenuItem key={t.code} value={t.code}>{t.libelle}</MenuItem>
              ))}
          </Select>
        </FormControl>

        {/* Ordre */}
        <TextField
          label="Ordre"
          type="number"
          size="small"
          value={formData.ordre ?? 0}
          onChange={(e) => setFormData(prev => ({ ...prev, ordre: Number(e.target.value) }))}
          inputProps={{ min: 0 }}
        />
      </Box>

      <Box mt={2} display="flex" gap={1}>
        <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleSubmit}>
          {selectedWorkflowEtape ? 'Modifier' : 'Ajouter'}
        </MyButton>
        {selectedWorkflowEtape && (
          <MyButton actionType={ButtonType.DELETE} variant="outlined" onClick={handleDelete}>
            Supprimer
          </MyButton>
        )}
        <MyButton actionType={ButtonType.CANCEL} variant="outlined" onClick={reset}>
          Annuler
        </MyButton>
      </Box>
    </Paper>
  );
};

export default WorkflowEtapeForm;
